import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { applyOrgTheme } from "./fbs-common";
import {
    getStaticFbsText,
    resolveTemplateLabel,
    getLanguageDirection,
} from "./fbsTranslations";

export function FbsOrgLanding({
    template,
    onNavigateToFeedback,
    categoryRatings,
    setCategoryRatings,
    categoryIssues,
    setCategoryIssues,
    selectedLanguage,
}) {
    // Use passed state from parent component instead of local state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const categories = Array.isArray(template?.content?.categories)
        ? template.content.categories
        : [];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        // Initialize ratings only if they haven't been set already (preserve state on navigation)
        if (
            template &&
            categories.length > 0 &&
            Object.keys(categoryRatings).length === 0
        ) {
            const initialRatings = {};
            categories.forEach((category) => {
                initialRatings[category.id] = 0;
            });
            setCategoryRatings(initialRatings);
        }
    }, [template, categories, categoryRatings, setCategoryRatings]);

    // Theme logic - apply organization-specific themes
    useEffect(() => {
        if (template && template.org) {
            applyOrgTheme(template.org.name, template.org.shortname);
        }
    }, [template]);

    const handleRating = (categoryId, rating) => {
        setCategoryRatings((prev) => ({
            ...prev,
            [categoryId]: rating,
        }));

        // Reset issues when rating changes
        setCategoryIssues((prev) => ({
            ...prev,
            [categoryId]: [],
        }));
    };

    const toggleIssue = (categoryId, issue) => {
        setCategoryIssues((prev) => {
            const currentIssues = prev[categoryId] || [];
            const issueId =
                typeof issue === "object" ? issue.id || issue.name : issue;

            const issueExists = currentIssues.some((i) => {
                const existingIssueId =
                    typeof i === "object" ? i.id || i.name : i;
                return existingIssueId === issueId;
            });

            return {
                ...prev,
                [categoryId]: issueExists
                    ? currentIssues.filter((i) => {
                          const existingIssueId =
                              typeof i === "object" ? i.id || i.name : i;
                          return existingIssueId !== issueId;
                      })
                    : [...currentIssues, issue],
            };
        });
    };

    const isFormValid = () => {
        // Check if all categories have been rated
        return categories.every(
            (category) => categoryRatings[category.id] > 0,
        );
    };

    const calculateAverageRating = () => {
        const ratings = Object.values(categoryRatings);
        if (ratings.length === 0) return 0;

        const sum = ratings.reduce((acc, rating) => acc + rating, 0);
        return sum / ratings.length;
    };

    const handleNext = () => {
        const averageRating = calculateAverageRating();

        // Create a new structure with ratings and issues combined per category
        const categoriesData = categories.map((category) => ({
            id: category.id,
            name: category.name,
            rating: categoryRatings[category.id] || 0,
            issues: (categoryIssues[category.id] || []).map((issue) => {
                if (typeof issue === "object") {
                    return {
                        id: issue.id,
                        name: issue.name,
                        icon: issue.icon ?? null,
                        item_id: issue.item_id ?? null,
                        item_name: issue.item_name ?? null,
                    };
                }

                return { name: issue };
            }),
        }));

        // Store the data for the feedback page
        const feedbackData = {
            rating: averageRating,
            categories: categoriesData,
        };

        // Use navigation function to go to feedback page
        if (typeof onNavigateToFeedback === "function") {
            onNavigateToFeedback(feedbackData);
        } else {
            console.warn(
                "onNavigateToFeedback function not provided to FbsOrgLanding",
            );
        }
    };

    const renderStars = (categoryId) => {
        const maxRating = 5;
        const currentRating = categoryRatings[categoryId] || 0;

        return (
            <div className="stars-container">
                {[...Array(maxRating)].map((_, index) => {
                    const starValue = index + 1;
                    return (
                        <span
                            key={index}
                            className={`star-rating ${starValue <= currentRating ? "filled" : ""}`}
                            onClick={() => handleRating(categoryId, starValue)}
                        >
                            <Icon
                                icon="mdi:star"
                                color={
                                    starValue <= currentRating
                                        ? "#ffd700"
                                        : "#ccc"
                                }
                            />
                        </span>
                    );
                })}
            </div>
        );
    };

    const renderIssues = (category) => {
        const rating = categoryRatings[category.id] || 0;
        const issues = category.issues || [];
        const threshold = template?.meta?.threshold ?? 3;
        const showIssues =
            rating > 0 && rating <= threshold && issues && issues.length > 0;

        // Return the issues container with conditional class for animation
        return (
            <div className={`category-issues ${!showIssues ? "hidden" : ""}`}>
                {showIssues && (
                    <>
                        <h5>
                            {getStaticFbsText(
                                "screen.whatIssuesExperienced",
                                selectedLanguage,
                            )}
                        </h5>
                        <div
                            className="issues-grid"
                            style={{ paddingTop: "8px" }}
                        >
                            {issues.map((issue, index) => {
                                // Handle both string issues and object issues
                                const issueName =
                                    typeof issue === "object"
                                        ? issue.name
                                        : issue;
                                const issueIcon =
                                    typeof issue === "object"
                                        ? issue.icon
                                        : null;
                                const isChecked =
                                    categoryIssues[category.id]?.some((i) => {
                                        const existingIssueId =
                                            typeof i === "object"
                                                ? i.id || i.name
                                                : i;
                                        return (
                                            existingIssueId ===
                                            (typeof issue === "object"
                                                ? issue.id || issue.name
                                                : issue)
                                        );
                                    }) || false;

                                return (
                                    <label
                                        key={index}
                                        className="issue-checkbox-container"
                                    >
                                        <input
                                            type="checkbox"
                                            className="issue-checkbox"
                                            checked={isChecked}
                                            onChange={() =>
                                                toggleIssue(category.id, issue)
                                            }
                                        />
                                        <span className="issue-checkmark"></span>
                                        <div className="issue-content">
                                            {issueIcon && (
                                                <Icon
                                                    icon={issueIcon}
                                                    width={isMobile ? 16 : 20}
                                                    height={isMobile ? 16 : 20}
                                                    className="issue-icon"
                                                />
                                            )}
                                            <span className="issue-label">
                                                {typeof issue === "object"
                                                    ? resolveTemplateLabel(
                                                          issue,
                                                          selectedLanguage,
                                                      )
                                                    : issueName}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // If template is not loaded yet
    if (!template || categories.length === 0) {
        return (
            <div>{getStaticFbsText("screen.loading", selectedLanguage)}</div>
        );
    }

    const selectedLanguageDirection = getLanguageDirection(selectedLanguage);

    return (
        <div className="org-ratings-container" dir={selectedLanguageDirection}>
            <div className="org-rating-header">
                <h3>
                    {getStaticFbsText(
                        "screen.pleaseRateServices",
                        selectedLanguage,
                    )}
                </h3>
            </div>

            <div className="categories-container">
                {categories.map((category, index) => (
                    <div key={category?.id ?? index} className="category-card">
                        <div className="category-wrapper">
                            <div className="category-name-container">
                                {category.icon && (
                                    <Icon
                                        icon={category.icon}
                                        width={isMobile ? 24 : 32}
                                        height={isMobile ? 24 : 32}
                                        className="category-icon"
                                    />
                                )}
                                <h4 className="category-name">
                                    {resolveTemplateLabel(
                                        category,
                                        selectedLanguage,
                                    )}
                                </h4>
                            </div>
                            {renderStars(category.id)}
                        </div>
                        {renderIssues(category)}
                    </div>
                ))}
            </div>

            <div className="navigation-buttons">
                <button
                    className="next-button"
                    onClick={handleNext}
                    disabled={!isFormValid()}
                >
                    {getStaticFbsText("action.next", selectedLanguage)}
                </button>
            </div>
        </div>
    );
}

